/**
 * /settings/users (admin-only) — gestão de usuários no padrão TiFlux:
 * breadcrumb "Configurações / Usuários / Usuários", tabela Nome | E-mail |
 * Papel | Status | Ações, busca, aba e paginação no servidor (busca, aba e
 * página ficam na URL, como em Clientes), criação e edição em dialog.
 *
 * Contratos (packages/shared/src/auth.ts + apps/api users.routes.ts):
 * - GET  /users?q&kind&limit&offset → User[] + X-Total-Items — as pessoas
 *   DESTE tenant, equipe e portal (F-108; usa usersListQuery, 50 por página);
 *   `kind` = team | portal, a aba (spec 034)
 * - GET  /users/counts?q   → { all, team, portal } — o número de cada aba
 * - POST /users            → { name, email, role, password } (409 email_already_in_use);
 *   no modo keycloak SEM password (F-174: a conta do SSO nasce sem senha, e o
 *   diálogo abre o "Redefinir senha" da pessoa recém-criada). F-180: o e-mail de
 *   quem já existe no ERP sem acesso aqui volta `adopted: true` — a pessoa foi
 *   adotada (a mesma linha), e o "Redefinir senha" abre dizendo isso
 * - PATCH /users/:id       → { name?, email?, role?, active?, password? } (strict;
 *   pessoa de outro tenant é 404; `password` é 422 no modo keycloak). `active`
 *   é o acesso A ESTA empresa (spec 042); a resposta diz em `account` se a
 *   conta de login inteira acompanhou (era o último acesso) ou se o SSO falhou
 * - POST /users/:id/password-reset → a senha temporária, UMA vez (spec 034)
 * - GET  /users/client-links?userIds → os clientes dos logins de portal da
 *   página (coluna "Cliente"); GET/POST/DELETE /users/:id/clients no diálogo
 *   de edição do login (spec 034, 2/2)
 * Usuários role 'client' são do portal: listados com papel "Cliente (portal)"
 * e sem troca de papel na edição (promover portal→staff não é fluxo daqui).
 */
import { useEffect, useState, type FormEvent } from 'react';
import { z } from 'zod';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { Eye, KeyRound, Pencil, Plus, Search, ShieldAlert } from 'lucide-react';
import {
  USERS_PAGE_SIZE,
  type User,
  type UserCreateBody,
  type UserCreateResponse,
  type UserKeycloakCreateBody,
  type UserKind,
  type UserPatchBody,
  type UserPatchResponse,
  type UserRole,
  type UsersCounts,
} from '@erp/shared';
import { api } from '@/lib/api-client';
import { isKeycloakMode } from '@/config/env';
import { useAuthUser } from '@/auth/use-auth';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge, Dialog, EmptyState, Skeleton } from '@/components/ui/misc';
import { pageCountOf, Pagination } from '@/components/ui/pagination';
import { cn } from '@/lib/utils';
import {
  ADOPTION_HINT,
  adoptedNotice,
  LOGIN_ACCOUNT_ERRORS,
  LOGIN_ACCOUNT_HINT,
  ROLE_CHANGE_ERRORS,
} from './login-account';
import { useTransientToast } from '@/components/ui/transient-toast';
import { usersListQuery } from '@/features/tickets/tickets-queries';
import { permissionGroupsListOptions } from '@/features/permissions/permissions-queries';
import {
  lastSeenByUser,
  lastSeenText,
  memberAccessQuery,
  weeklyAccessSummary,
} from '@/features/settings/member-access';
import { usePermissions } from '@/auth/use-permissions';
import { capitalize, useTerms } from '@/shared/tenant-profile';
import {
  ImpersonateDialog,
  type ImpersonationTargetRef,
} from '@/features/impersonation/ImpersonateDialog';
import { PasswordResetDialog } from './PasswordResetDialog';
import { LoginClientsSection } from './LoginClientsSection';
import { userClientLinksQuery } from './user-clients-queries';
import { clientCellText, clientNamesByUser } from './user-clients';
import { accountChangeNotice, type AccessNotice } from './user-access';

/** As abas da tela (spec 034), na ordem pedida; "Todos" é o padrão. */
const KIND_TABS = [
  { value: 'team', label: 'Equipe' },
  { value: 'portal', label: 'Clientes do portal' },
  { value: 'all', label: 'Todos' },
] as const;
type KindTab = (typeof KIND_TABS)[number]['value'];

/**
 * A busca, a aba e a página da tela na URL — como em Clientes (voltar e
 * recarregar mantêm). Valor de aba desconhecido na URL cai em "Todos".
 */
export const usersSearchSchema = z.object({
  q: z.string().default(''),
  page: z.coerce.number().int().min(1).default(1),
  kind: z.enum(['all', 'team', 'portal']).catch('all').default('all'),
});

/** Os números das abas, com a mesma busca da lista (GET /users/counts). */
function usersCountsQuery(q: string) {
  return queryOptions({
    queryKey: ['erp', 'users', 'counts', { q }] as const,
    staleTime: 60_000,
    queryFn: async ({ signal }) => {
      const res = await api<UsersCounts>('/users/counts', {
        query: { q: q.trim() || undefined },
        signal,
      });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

const kindOf = (tab: KindTab): UserKind | undefined => (tab === 'all' ? undefined : tab);

const routeApi = getRouteApi('/_authenticated/settings/users');

const ROLE_LABELS: Record<UserRole, string> = {
  admin: 'Admin',
  attendant: 'Atendente',
  client: 'Cliente (portal)',
};

const ROLE_BADGE_VARIANT: Record<UserRole, 'primary' | 'info' | 'default'> = {
  admin: 'primary',
  attendant: 'info',
  client: 'default',
};

/** Códigos da API de usuários → mensagens amigáveis. */
const ERROR_LABELS: Record<string, string> = {
  email_already_in_use: 'Este e-mail já está em uso por outro usuário.',
  user_not_found: 'Usuário não encontrado — a lista pode estar desatualizada.',
  validation_error: 'Dados inválidos — revise os campos e tente de novo.',
  forbidden: 'Você não tem permissão para esta ação.',
  insert_failed: 'Não foi possível criar o usuário. Tente novamente.',
  // spec 042: desligar o próprio acesso trancaria você fora na hora
  deactivate_self: 'Você não pode desativar o seu próprio acesso.',
  // F-174: criar a pessoa cria a conta dela no SSO — se ele falha, ninguém é criado
  ...LOGIN_ACCOUNT_ERRORS,
};

/** spec 062: na edição, o SSO falhando não "deixa de criar" ninguém — nada é alterado */
const EDIT_ERROR_LABELS: Record<string, string> = { ...ERROR_LABELS, ...ROLE_CHANGE_ERRORS };

function errorMessage(err: unknown, labels: Record<string, string> = ERROR_LABELS): string {
  if (
    err !== null &&
    typeof err === 'object' &&
    'error' in err &&
    typeof (err as { error: unknown }).error === 'string'
  ) {
    const code = (err as { error: string }).error;
    return labels[code] ?? code;
  }
  return err instanceof Error ? err.message : 'Erro inesperado';
}

export function UsersPage() {
  const user = useAuthUser();

  // Tela INTEIRA admin-only (mesma regra de /settings/integrations): POST e
  // PATCH exigem admin na API — para os demais papéis só haveria erro 403.
  if (user?.role !== 'admin') {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-2 rounded-lg border bg-card text-center shadow-xs">
        <ShieldAlert className="size-8 text-muted-foreground" />
        <p className="text-sm font-medium">Acesso restrito</p>
        <p className="max-w-sm text-sm text-muted-foreground">
          Os usuários da conta são gerenciados por administradores. Fale com um administrador para
          criar ou alterar contas.
        </p>
      </div>
    );
  }

  return <UsersAdmin />;
}

function UsersAdmin() {
  const search = routeApi.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // a página anterior fica na tela enquanto a próxima chega (sem piscar o esqueleto)
  const usersQ = useQuery({
    ...usersListQuery({ q: search.q, page: search.page, kind: kindOf(search.kind) }),
    placeholderData: keepPreviousData,
  });
  // spec 034: o número de cada aba (mesma busca); a lista é paginada no servidor
  const countsQ = useQuery({ ...usersCountsQuery(search.q), placeholderData: keepPreviousData });
  const me = useAuthUser();
  // v7: coluna "Grupos de permissão" (somente leitura — edição pelo detalhe
  // do grupo, como no TiFlux): cruza GET /permission-groups por userIds.
  const permissionGroupsQ = useQuery(permissionGroupsListOptions());
  // spec 017 (US4): último acesso neste tenant — a tela inteira já é de admin
  const accessQ = useQuery(memberAccessQuery(true));
  const access = lastSeenByUser(accessQ.data);
  const week = weeklyAccessSummary(accessQ.data);
  const [term, setTerm] = useState(search.q);
  const [dialog, setDialog] = useState<{ user: User | null } | null>(null);
  // spec 034: redefinir senha — o diálogo guarda a senha só enquanto aberto;
  // F-180: `notice` é o aviso de que a pessoa recém-"criada" já existia
  const [resetting, setResetting] = useState<{ user: User; notice?: string } | null>(null);
  // spec 020: ver o portal como um usuário do cliente. Só quem é MEMBRO deste
  // tenant (está no último acesso) pode ser visto — o atendente que só o
  // espelho do TiFlux trouxe aparece na lista, mas não tem login de portal
  const { can } = usePermissions();
  const canImpersonate = can('users.impersonate');
  const terms = useTerms();
  const [impersonating, setImpersonating] = useState<ImpersonationTargetRef | null>(null);
  // spec 042: "era o último acesso — a conta de login foi desligada"
  const { toast, showToast } = useTransientToast('info', 6000);

  const permissionGroupNames = new Map<number, string[]>();
  for (const group of permissionGroupsQ.data ?? []) {
    for (const userId of group.userIds) {
      const names = permissionGroupNames.get(userId) ?? [];
      names.push(group.name);
      permissionGroupNames.set(userId, names);
    }
  }

  // a busca vai para a URL (e para o servidor) 350 ms depois da última tecla,
  // sempre de volta à página 1 — o mesmo ritmo da busca de Clientes
  useEffect(() => {
    const t = setTimeout(() => {
      if (term !== search.q) {
        void navigate({
          to: '/settings/users',
          search: { q: term, page: 1, kind: search.kind },
          replace: true,
        });
      }
    }, 350);
    return () => clearTimeout(t);
  }, [term, search.q, search.kind, navigate]);

  const users = usersQ.data?.items ?? [];
  const total = usersQ.data?.total ?? 0;
  // spec 034 (2/2): a coluna "Cliente" — os vínculos dos logins de portal DESTA página
  const portalIds = users.filter((u) => u.role === 'client').map((u) => u.id);
  const linksQ = useQuery(userClientLinksQuery(portalIds));
  const clientNames = clientNamesByUser(linksQ.data);
  const pageCount = pageCountOf(total, USERS_PAGE_SIZE);
  const searching = search.q.trim() !== '';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <nav className="text-[13px] text-muted-foreground" aria-label="Breadcrumb">
            <Link to="/settings" className="transition-colors hover:text-foreground">
              Configurações
            </Link>
            <span className="mx-1.5">/</span>
            <span>Usuários</span>
            <span className="mx-1.5">/</span>
            <span className="text-foreground">Usuários</span>
          </nav>
          <h1 className="mt-2 flex items-center gap-2 text-xl leading-7 font-semibold">
            Usuários
            {usersQ.data && !searching ? (
              <span className="rounded-full bg-secondary px-2.5 py-0.5 text-xs font-medium text-secondary-foreground tabular-nums">
                {total}
              </span>
            ) : null}
          </h1>
          {accessQ.data ? (
            <p className="mt-1 text-[13px] text-muted-foreground">
              Entraram nos últimos 7 dias: {week.staff} de {week.staffTotal} da equipe ·{' '}
              {week.clients} de {week.clientsTotal} logins de cliente
            </p>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <input
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Buscar por nome ou e-mail"
              aria-label="Buscar usuário"
              className="h-9 w-56 rounded-md border border-border bg-card pl-3 pr-8 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground/70 focus:border-primary sm:w-64"
            />
            <Search className="pointer-events-none absolute top-1/2 right-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
          </div>
          <Button size="sm" onClick={() => setDialog({ user: null })}>
            <Plus className="size-4" strokeWidth={2.5} />
            Novo usuário
          </Button>
        </div>
      </div>

      {/* spec 034: Equipe × Clientes do portal × Todos — no servidor, na URL */}
      <div
        role="group"
        aria-label="Filtrar usuários"
        data-tour="usuarios-filtro"
        className="flex h-9 w-fit items-center rounded-md border bg-card p-0.5"
      >
        {KIND_TABS.map((tab) => {
          const n = countsQ.data?.[tab.value];
          return (
            <button
              key={tab.value}
              type="button"
              aria-pressed={search.kind === tab.value}
              onClick={() =>
                void navigate({
                  to: '/settings/users',
                  search: { q: search.q, page: 1, kind: tab.value },
                })
              }
              className={cn(
                'h-8 rounded-[5px] px-3 text-sm font-medium transition-colors',
                search.kind === tab.value
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {tab.label}
              {n !== undefined ? <span className="ml-1.5 tabular-nums opacity-80">{n}</span> : null}
            </button>
          );
        })}
      </div>

      <div className="overflow-hidden rounded-lg border bg-card shadow-xs">
        {usersQ.isPending ? (
          <div className="space-y-2 p-4" aria-busy>
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : usersQ.isError ? (
          <EmptyState message={`Erro ao carregar usuários: ${errorMessage(usersQ.error)}`} />
        ) : users.length === 0 ? (
          <EmptyState
            message={
              searching
                ? `Nenhum usuário para “${search.q.trim()}”`
                : total > 0
                  ? 'Nenhum usuário nesta página'
                  : search.kind === 'portal'
                    ? 'Nenhum login de portal'
                    : search.kind === 'team'
                      ? 'Ninguém na equipe'
                      : 'Nenhum usuário cadastrado'
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted text-left text-[13px]">
                  <th className="h-9 px-4 font-semibold">Nome</th>
                  <th className="h-9 px-4 font-semibold">E-mail</th>
                  <th className="h-9 w-36 px-4 font-semibold">Papel</th>
                  <th className="h-9 px-4 font-semibold" data-tour="usuarios-coluna-cliente">
                    {capitalize(terms.client)}
                  </th>
                  <th className="h-9 px-4 font-semibold">Grupos de permissão</th>
                  <th className="h-9 w-24 px-4 font-semibold">Status</th>
                  <th className="h-9 w-40 px-4 font-semibold">Último acesso</th>
                  <th className="h-9 w-28 px-4">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr
                    key={u.id}
                    className="border-b border-divider transition-colors last:border-0 hover:bg-accent/60"
                  >
                    <td className="max-w-52 truncate px-4 py-2.5 font-medium">{u.name}</td>
                    <td className="max-w-64 truncate px-4 py-2.5 text-muted-foreground">
                      {u.email}
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <Badge variant={ROLE_BADGE_VARIANT[u.role]}>{ROLE_LABELS[u.role]}</Badge>
                    </td>
                    <td className="max-w-56 px-4 py-2.5">
                      {(() => {
                        const cell =
                          u.role === 'client' ? clientCellText(clientNames.get(u.id)) : null;
                        if (!cell) {
                          return (
                            <span className="text-muted-foreground">
                              {u.role === 'client' && linksQ.data ? 'Sem vínculo' : '—'}
                            </span>
                          );
                        }
                        return (
                          <span
                            className="flex min-w-0 items-center gap-1.5"
                            title={(clientNames.get(u.id) ?? []).join('\n')}
                          >
                            <span className="truncate">{cell.first}</span>
                            {cell.more > 0 ? (
                              <Badge variant="outline" className="shrink-0 tabular-nums">
                                +{cell.more}
                              </Badge>
                            ) : null}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-2">
                      {(permissionGroupNames.get(u.id) ?? []).length === 0 ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {(permissionGroupNames.get(u.id) ?? []).map((name) => (
                            <Badge key={name} variant="outline">
                              {name}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <span
                        className={cn(
                          'text-[13px] font-medium',
                          u.active ? 'text-[#52C41A]' : 'text-muted-foreground'
                        )}
                      >
                        {u.active ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap text-[13px] text-muted-foreground">
                      {lastSeenText(access, u.id)}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex justify-end gap-1">
                        {canImpersonate && u.role === 'client' && u.active && access.has(u.id) ? (
                          <Button
                            variant="ghost"
                            size="icon-xs"
                            className="text-muted-foreground"
                            title={`Ver como ${terms.client}`}
                            aria-label={`Ver como ${terms.client}: ${u.name}`}
                            onClick={() => setImpersonating({ id: u.id, name: u.name, email: u.email })}
                          >
                            <Eye className="size-3.5" />
                          </Button>
                        ) : null}
                        {me?.id !== u.id ? (
                          <Button
                            variant="ghost"
                            size="icon-xs"
                            className="text-muted-foreground"
                            title={
                              u.active
                                ? 'Redefinir senha'
                                : 'Usuário inativo — ative antes de redefinir a senha'
                            }
                            aria-label={`Redefinir senha: ${u.name}`}
                            data-tour="usuarios-redefinir-senha"
                            disabled={!u.active}
                            onClick={() => setResetting({ user: u })}
                          >
                            <KeyRound className="size-3.5" />
                          </Button>
                        ) : null}
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          className="text-muted-foreground"
                          title="Editar usuário"
                          onClick={() => setDialog({ user: u })}
                        >
                          <Pencil className="size-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {/* Rodapé: contagem + paginação — a lista nunca é cortada em silêncio */}
        {usersQ.data ? (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t px-4 py-3">
            <span className="text-[13px] text-muted-foreground">
              {total} {search.kind === 'portal' ? 'login' : 'usuário'}
              {total === 1 ? '' : 's'}
              {search.kind === 'portal' ? ' de portal' : search.kind === 'team' ? ' da equipe' : ''}
              {searching ? ` para “${search.q.trim()}”` : ''} — página {search.page} de{' '}
              {pageCount}
            </span>
            <Pagination
              page={search.page}
              pageCount={pageCount}
              onPage={(page) =>
                void navigate({
                  to: '/settings/users',
                  search: { q: search.q, page, kind: search.kind },
                })
              }
            />
          </div>
        ) : null}
      </div>

      {dialog ? (
        dialog.user ? (
          <EditUserDialog
            user={dialog.user}
            canResetPassword={me?.id !== dialog.user.id}
            isSelf={me?.id === dialog.user.id}
            onSaved={showToast}
            onClose={() => setDialog(null)}
            onResetPassword={(target) => {
              setDialog(null);
              setResetting({ user: target });
            }}
          />
        ) : (
          <CreateUserDialog
            onClose={() => setDialog(null)}
            onCreated={(created) => {
              // F-174: no SSO a conta nasce sem senha — o próximo passo é a temporária.
              // F-180: a pessoa adotada também não tem a senha digitada (no modo local
              // ela não é gravada): o "Redefinir senha" abre nos dois modos, avisando
              setDialog(null);
              if (created.adopted) setResetting({ user: created, notice: adoptedNotice(created) });
              else if (isKeycloakMode) setResetting({ user: created });
            }}
          />
        )
      ) : null}
      {impersonating ? (
        <ImpersonateDialog target={impersonating} onClose={() => setImpersonating(null)} />
      ) : null}
      {resetting ? (
        <PasswordResetDialog
          user={resetting.user}
          notice={resetting.notice}
          onClose={() => setResetting(null)}
          onLoginCreated={() => void queryClient.invalidateQueries({ queryKey: ['erp', 'users'] })}
        />
      ) : null}
      {toast}
    </div>
  );
}

function CreateUserDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (user: UserCreateResponse) => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<UserRole>('attendant');

  const create = useMutation({
    mutationFn: async (body: UserCreateBody | UserKeycloakCreateBody) => {
      const res = await api<UserCreateResponse>('/users', { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ['erp', 'users'] });
      onCreated(created);
    },
  });

  // F-174: em SSO a senha não é daqui — o corpo NÃO leva password (a API recusa, 422)
  const valid =
    name.trim().length > 0 && email.trim().length > 0 && (isKeycloakMode || password.length >= 8);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    const base = { name: name.trim(), email: email.trim(), role };
    create.mutate(isKeycloakMode ? base : { ...base, password });
  };

  return (
    <Dialog open onClose={onClose} title="Novo usuário">
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="user-name">
            Nome <span className="text-destructive">*</span>
          </Label>
          <Input
            id="user-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="user-email">
            E-mail <span className="text-destructive">*</span>
          </Label>
          <Input
            id="user-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        {isKeycloakMode ? (
          <p className="rounded-md border bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
            {LOGIN_ACCOUNT_HINT} {ADOPTION_HINT}
          </p>
        ) : (
          <div className="space-y-1.5">
            <Label htmlFor="user-password">
              Senha <span className="text-destructive">*</span>
            </Label>
            <Input
              id="user-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={8}
              required
              autoComplete="new-password"
            />
            <p className="text-xs text-muted-foreground">Mínimo de 8 caracteres.</p>
          </div>
        )}
        <div className="space-y-1.5">
          <Label>Papel</Label>
          <Select value={role} onValueChange={(v) => setRole(v as UserRole)}>
            <SelectTrigger size="sm" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="attendant">Atendente</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {create.error ? (
          <p className="text-sm text-destructive">{errorMessage(create.error)}</p>
        ) : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={create.isPending || !valid}>
            {create.isPending ? 'Criando…' : 'Criar usuário'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function EditUserDialog({
  user,
  canResetPassword,
  isSelf,
  onSaved,
  onClose,
  onResetPassword,
}: {
  user: User;
  /** falso para a própria pessoa: a senha dela se troca em Minha conta */
  canResetPassword: boolean;
  /** a própria pessoa: o "Ativo" fica travado (spec 042) */
  isSelf: boolean;
  /** aviso que sobrevive ao diálogo fechado (o toast da página) */
  onSaved: (message: string) => void;
  onClose: () => void;
  onResetPassword: (user: User) => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(user.name);
  const [role, setRole] = useState<UserRole>(user.role);
  const [active, setActive] = useState(user.active);
  const [password, setPassword] = useState('');
  // spec 042: o SSO não acompanhou — o diálogo fica aberto, e salvar de novo tenta
  const [notice, setNotice] = useState<AccessNotice | null>(null);

  const isClient = user.role === 'client';

  const save = useMutation({
    mutationFn: async (body: UserPatchBody) => {
      const res = await api<UserPatchResponse>(`/users/${user.id}`, { method: 'PATCH', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['erp', 'users'] });
      const next = accountChangeNotice(saved.name, saved.account);
      if (next?.keepOpen) {
        setNotice(next);
        return;
      }
      if (next) onSaved(next.text);
      onClose();
    },
  });

  const valid = name.trim().length > 0 && (password.length === 0 || password.length >= 8);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    const body: UserPatchBody = { name: name.trim(), active };
    // Cliente do portal permanece cliente — o papel só muda entre staff.
    if (!isClient) body.role = role;
    if (!isKeycloakMode && password.length > 0) body.password = password;
    save.mutate(body);
  };

  return (
    <Dialog open onClose={onClose} title="Editar usuário">
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="edit-user-name">
            Nome <span className="text-destructive">*</span>
          </Label>
          <Input
            id="edit-user-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
          />
        </div>
        <div className="space-y-1.5">
          <Label>E-mail</Label>
          <p className="truncate text-sm text-muted-foreground">{user.email}</p>
        </div>
        <div className="space-y-1.5">
          <Label>Papel</Label>
          {isClient ? (
            <div>
              <Badge variant="default">Cliente (portal)</Badge>
              <p className="mt-1 text-xs text-muted-foreground">
                Usuários do portal do cliente não trocam de papel por aqui.
              </p>
            </div>
          ) : (
            <Select value={role} onValueChange={(v) => setRole(v as UserRole)}>
              <SelectTrigger size="sm" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="attendant">Atendente</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
        <div className="space-y-1">
          <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
            <Switch checked={active} onCheckedChange={setActive} disabled={isSelf} />
            Ativo nesta empresa
          </label>
          <p className="text-xs text-muted-foreground">
            {isSelf
              ? 'Você não pode desativar o seu próprio acesso.'
              : 'Desativado, deixa de entrar nesta empresa na hora. Se não tiver acesso a nenhuma outra, a conta de login também é desligada.'}
          </p>
        </div>
        {/* spec 034: no modo keycloak a senha é do SSO — o campo gravava um hash
            que ninguém lê (200, e a senha continuava a mesma). Lá, o caminho é
            "Redefinir senha": temporária, entregue por fora, criada no 1º acesso. */}
        {isKeycloakMode ? null : (
          <div className="space-y-1.5">
            <Label htmlFor="edit-user-password">Nova senha</Label>
            <Input
              id="edit-user-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Deixe em branco para manter a atual"
              autoComplete="new-password"
            />
            {password.length > 0 && password.length < 8 ? (
              <p className="text-xs text-destructive">Mínimo de 8 caracteres.</p>
            ) : null}
          </div>
        )}
        {/* spec 034 (2/2): os clientes do login de portal — valem na hora */}
        {isClient ? <LoginClientsSection userId={user.id} /> : null}
        {canResetPassword ? (
          <div className="flex items-center justify-between gap-3 rounded-md border px-3 py-2">
            <div>
              <p className="text-sm font-medium">Senha</p>
              <p className="text-xs text-muted-foreground">
                Gera uma senha temporária para você enviar à pessoa.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!user.active}
              title={user.active ? undefined : 'Usuário inativo — ative antes de redefinir a senha'}
              onClick={() => onResetPassword(user)}
            >
              <KeyRound className="size-4" />
              Redefinir senha
            </Button>
          </div>
        ) : null}
        {save.error ? <p className="text-sm text-destructive">{errorMessage(save.error, EDIT_ERROR_LABELS)}</p> : null}
        {notice ? (
          <p role="alert" className="text-sm text-destructive">
            {notice.text}
          </p>
        ) : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            {notice ? 'Fechar' : 'Cancelar'}
          </Button>
          <Button type="submit" size="sm" disabled={save.isPending || !valid}>
            {save.isPending ? 'Salvando…' : notice ? 'Salvar de novo' : 'Salvar'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
